import { cached } from "@base/util/Cache";
import { mapCount, PartialRecord } from "@base/util/TypeUtil";
import { SST } from "@biz/definitions/SST";
import { EdgeName, ISunSailSide, PoleId, PoleIndex, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { getEdgeName } from "@biz/definitions/SunSailDefinitions.util";

export interface ISunSailTopologyGroup {
  poleIndices: number[];
  nonAxleIndices: PoleIndex[];
}

export interface IEdgeInfo {
  netBorder?: boolean;
  axle?: boolean;
  keder?: boolean;
  netReversed?: boolean;
}
export interface ISunSailTopology {
  edgeNames?: EdgeName[];
  edgeInfos: PartialRecord<EdgeName, IEdgeInfo>; // new
  sides: ISunSailSide[];
  axleIndices?: PoleIndex[];
  groups: ISunSailTopologyGroup[];
}

export class SunSailTopology {
  private static cache: Record<string, ISunSailTopology> = {};

  static calcTopgraphy3WithAxle(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true },
        "bc": { netBorder: true },
        "ac": { netBorder: true, axle: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.A, netBorder: true, axle: true, groupIndices: [0] },
      ],
      axleIndices: [PoleIndex.A, PoleIndex.C],
      groups: [{ poleIndices: [0, 1, 2], nonAxleIndices: [1] }]
    }
  }

  static calcTopgraphy4(withAxle: boolean): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true },
        "bc": { netBorder: true },
        "ac": { axle: withAxle },
        "cd": { netBorder: true },
        "ad": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.A, second: PoleIndex.C, netBorder: false, axle: withAxle, groupIndices: [0, 1] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [1] },
        { first: PoleIndex.D, second: PoleIndex.A, netBorder: true, groupIndices: [1] },
      ],
      ...(withAxle ? { axleIndices: [PoleIndex.A, PoleIndex.C] } : undefined),
      groups: [{ poleIndices: [0, 1, 2], nonAxleIndices: [1] }, { poleIndices: [0, 3, 2], nonAxleIndices: [3] }]
    }
  }

  static calcTopgraphy3Fixed(): ISunSailTopology {
    return {
      edgeNames: ["ab", "bc", "ac"],
      edgeInfos: {
        "ab": { netBorder: true },
        "bc": { netBorder: true },
        "ac": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
      ],
      groups: [{ poleIndices: [0, 1, 2], nonAxleIndices: [0, 1, 2] }]
    }
  }

  static calcTopgraphyConcept4(): ISunSailTopology {
    return {
      edgeNames: ["ab", "bc", "cd", "ad"],
      edgeInfos: {
        "ab": { netBorder: true },
        "bc": { netBorder: true },
        "ac": {},
        "cd": { netBorder: true },
        "ad": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.A, second: PoleIndex.C, netBorder: false, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.D, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
      ],
      groups: [{ poleIndices: [0, 1, 2, 3], nonAxleIndices: [0, 1, 2, 3] }]
    }
  }

  static calcTopgraphyConcept5(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true },
        "bc": { netBorder: true },
        "cd": { netBorder: true },
        "de": { netBorder: true },
        "ae": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.D, second: PoleIndex.E, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.E, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
      ],
      groups: [{ poleIndices: [0, 1, 2, 3, 4], nonAxleIndices: [0, 1, 2, 3, 4] }]
    }
  }

  static calcTopgraphyConcept6(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true },
        "bc": { netBorder: true },
        "cd": { netBorder: true },
        "de": { netBorder: true },
        "ef": { netBorder: true },
        "af": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.D, second: PoleIndex.E, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.E, second: PoleIndex.F, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.F, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
      ],
      groups: [{ poleIndices: [0, 1, 2, 3, 4, 5], nonAxleIndices: [0, 1, 2, 3, 4, 5] }]
    };
  }

  static calcTopgraphyCS3(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true, axle: true },
        "bc": { netBorder: true },
        "ac": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, axle: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
      ],
      axleIndices: [PoleIndex.A, PoleIndex.B],
      groups: [{ poleIndices: [0, 1, 2], nonAxleIndices: [2] }]
    };
  }

  static calcTopgraphyCS4(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true, axle: true },
        "bc": { netBorder: true },
        "cd": { netBorder: true, keder: true },
        "ad": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, axle: true, groupIndices: [0] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.D, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
      ],
      axleIndices: [PoleIndex.A, PoleIndex.B],
      groups: [{ poleIndices: [0, 1, 2, 3], nonAxleIndices: [2, 3] }]
    };
  }

  static calcTopgraphyCS5(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true, axle: true },
        "bc": { netBorder: true },
        "cd": { netBorder: true, keder: true },
        "ad": { netBorder: true, netReversed: true },
        "ae": { netBorder: true },
        "be": { netBorder: true, netReversed: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, axle: true, groupIndices: [0, 1] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.D, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.A, second: PoleIndex.E, netBorder: true, groupIndices: [1] },
        { first: PoleIndex.E, second: PoleIndex.B, netBorder: true, groupIndices: [1] },
      ],
      axleIndices: [PoleIndex.A, PoleIndex.B],
      groups: [{ poleIndices: [0, 1, 2, 3], nonAxleIndices: [2, 3] }, { poleIndices: [0, 1, 4], nonAxleIndices: [4] }]
    };
  }

  static calcTopgraphyCS6(): ISunSailTopology {
    return {
      edgeInfos: {
        "ab": { netBorder: true, axle: true },
        "bc": { netBorder: true },
        "cd": { netBorder: true, keder: true },
        "ad": { netBorder: true, netReversed: true },
        "af": { netBorder: true },
        "ef": { netBorder: true, keder: true, netReversed: true },
        "be": { netBorder: true },
      },
      sides: [
        { first: PoleIndex.A, second: PoleIndex.B, netBorder: true, axle: true, groupIndices: [0, 1] },
        { first: PoleIndex.B, second: PoleIndex.C, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.C, second: PoleIndex.D, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.D, second: PoleIndex.A, netBorder: true, groupIndices: [0] },
        { first: PoleIndex.A, second: PoleIndex.F, netBorder: true, groupIndices: [1] },
        { first: PoleIndex.E, second: PoleIndex.F, netBorder: true, groupIndices: [1] },
        { first: PoleIndex.B, second: PoleIndex.E, netBorder: true, groupIndices: [1] },
      ],
      axleIndices: [PoleIndex.A, PoleIndex.B],
      groups: [{ poleIndices: [0, 1, 2, 3], nonAxleIndices: [2, 3] }, { poleIndices: [0, 1, 4, 5], nonAxleIndices: [4, 5] }]
    };
  }

  static findGroupsForSide(a: PoleId, b: PoleId, groups: ISunSailTopology["groups"]): number[] {
    const ret: number[] = [];
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi];
      if (g.poleIndices.includes(a) && g.poleIndices.includes(b))
        ret.push(gi);
    }
    return ret;
  }

  static calcTopologySandy(poleCount: number): ISunSailTopology {
    if (poleCount < 3)
      throw new Error("At least 3 poles required!");
    // handle patches
    // handle poles in the middle
    // triangulation...

    const groups: ISunSailTopology["groups"] = [];
    const edgeInfos: ISunSailTopology["edgeInfos"] = {};
    const sides: ISunSailTopology["sides"] = [];

    const poleIndices = mapCount(poleCount, (i) => i);
    groups.push({ poleIndices, nonAxleIndices: poleIndices })
    for (let i = 0; i < poleCount; i++) {
      const first: PoleIndex = i;
      const second: PoleIndex = (i + 1) % poleCount;
      sides.push({ first, second, netBorder: true, groupIndices: [0] });
    }

    // calculate all diagonals for a polygon with n corners
    for (let i = 0; i < poleCount; i++) {
      for (let j = i + 1; j < poleCount; j++) {
        const first: PoleIndex = i;
        const second: PoleIndex = j;
        const en = getEdgeName(first, second);
        const ei: IEdgeInfo = {};
        if (first + 1 === second || (second + 1) % poleCount === first) {
          ei.netBorder = true;
          ei.netReversed = i === 0 && j === poleCount - 1;
        }
        edgeInfos[en] = ei;
      }
    }

    return {
      edgeInfos,
      sides,
      groups
    };
  }


  static calcTopology(sunSailType: SunSailType, poleCount: number): ISunSailTopology {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayC:
      case SunSailType.SolidayM:
      case SunSailType.SolidayX:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayFlex:
        return poleCount === 3
          ? cached("topology3WithAxle", SunSailTopology.cache, SunSailTopology.calcTopgraphy3WithAxle)
          : cached("topology4WithAxle", SunSailTopology.cache, () => SunSailTopology.calcTopgraphy4(true));
      case SunSailType.Fixed:
      case SunSailType.Standard:
        return poleCount === 3
          ? cached("topology3Fixed", SunSailTopology.cache, SunSailTopology.calcTopgraphy3Fixed)
          : cached("topologyConcept4", SunSailTopology.cache, SunSailTopology.calcTopgraphyConcept4)

      case SunSailType.SolidayCS:
      case SunSailType.SolidayXS:
        return poleCount === 3
          ? cached("topologyCS3", SunSailTopology.cache, SunSailTopology.calcTopgraphyCS3)
          : cached("topologyCS4", SunSailTopology.cache, SunSailTopology.calcTopgraphyCS4)

      case SunSailType.SolidayTwin:
        return poleCount === 5
          ? cached("topologyCS5", SunSailTopology.cache, SunSailTopology.calcTopgraphyCS5)
          : cached("topologyCS6", SunSailTopology.cache, SunSailTopology.calcTopgraphyCS6)

      case SunSailType.Concept: {
        switch (poleCount) {
          case 4:
            return cached("topologyConcept4", SunSailTopology.cache, SunSailTopology.calcTopgraphyConcept4)
          case 5:
            return cached("topologyConcept5", SunSailTopology.cache, SunSailTopology.calcTopgraphyConcept5)
          case 6:
            return cached("topologyConcept6", SunSailTopology.cache, SunSailTopology.calcTopgraphyConcept6)
        }
      }
    }

    if (SST.is(sunSailType, SST.Sandy)) {
      return cached("topologySandy" + poleCount, SunSailTopology.cache, () => SunSailTopology.calcTopologySandy(poleCount))
    }

    return poleCount === 3 ? cached("topology3Fixed", SunSailTopology.cache, SunSailTopology.calcTopgraphy3Fixed) : cached("topologyConcept4", SunSailTopology.cache, SunSailTopology.calcTopgraphyConcept4);
  }
}